import { useCallback, useEffect, useRef, useState } from "react";
import { captureException, captureMessage } from "@sentry/react";

import LocalStorageService from "core/services/local-storage-service";

import { nexaSocketUrl } from "./urls";
import { toNexaEvent } from "./wire";
import type { WireEvent } from "./wire";

import {
  CHAT_WS_SUBPROTOCOL,
  NEXA_MAX_RECONNECT_ATTEMPTS,
  NEXA_RECONNECT_BASE_DELAY_MS,
  NEXA_RECONNECT_MAX_DELAY_MS,
  NEXA_RECONNECT_STABLE_MS,
} from "./constants";
import {
  NEXA_CLOSE_FORBIDDEN,
  NEXA_CLOSE_UNAUTHENTICATED,
  type NexaCloseReason,
  type NexaEvent,
  type NexaMessage,
  type TurnOutcome,
  type TurnPhase,
  type TurnStatus,
} from "./types";

interface UseNexaTurnOptions {
  threadId: string | null;
  enabled?: boolean;
  onTurnComplete?: () => void;
}

export interface UseNexaTurn {
  status: TurnStatus;
  phases: TurnPhase[];
  messages: NexaMessage[];
  closeReason: NexaCloseReason | null;
  needsResume: boolean;
  lastError: string | null;
  send: (text: string) => void;
  hasPending: boolean;
  cancelPending: () => void;
  reconnect: () => void;
  setMessages: React.Dispatch<React.SetStateAction<NexaMessage[]>>;
  clearNeedsResume: () => void;
}

let messageSeq = 0;
const nextId = (prefix: string) => `${prefix}-${Date.now()}-${(messageSeq += 1)}`;

export function useNexaTurn({ threadId, enabled = true, onTurnComplete }: UseNexaTurnOptions): UseNexaTurn {
  const [status, setStatus] = useState<TurnStatus>("connecting");
  const [phases, setPhases] = useState<TurnPhase[]>([]);
  const [messages, setMessages] = useState<NexaMessage[]>([]);
  const [closeReason, setCloseReason] = useState<NexaCloseReason | null>(null);
  const [needsResume, setNeedsResume] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [hasPending, setHasPending] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stableTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptsRef = useRef(0);
  const openRef = useRef<((tid: string) => void) | null>(null);
  const runningRef = useRef(false);
  const readyRef = useRef(false);
  const queuedRef = useRef<string | null>(null);
  const pendingIdRef = useRef<string | null>(null);
  const phasesRef = useRef<TurnPhase[]>([]);
  const threadIdRef = useRef<string | null>(null);
  const enabledRef = useRef(enabled);
  const prevThreadRef = useRef<string | null>(null);
  const onTurnCompleteRef = useRef(onTurnComplete);

  const sessionId = LocalStorageService.getService().getSessionID();

  useEffect(() => {
    phasesRef.current = phases;
  }, [phases]);
  useEffect(() => {
    threadIdRef.current = threadId;
  }, [threadId]);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);
  useEffect(() => {
    onTurnCompleteRef.current = onTurnComplete;
  }, [onTurnComplete]);

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (stableTimerRef.current) {
      clearTimeout(stableTimerRef.current);
      stableTimerRef.current = null;
    }
  }, []);

  const closeSocket = useCallback(() => {
    clearTimers();
    const ws = wsRef.current;
    if (!ws) return;
    wsRef.current = null;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close(1000, "Normal Closure");
    }
  }, [clearTimers]);

  const settleTurn = useCallback((outcome: TurnOutcome) => {
    runningRef.current = false;
    const now = Date.now();
    const finished = phasesRef.current.map((p) => (p.endedAt ? p : { ...p, endedAt: now }));
    setMessages((prev) => [...prev, { id: nextId("a"), role: "assistant", outcome, phases: finished, at: now }]);
    setPhases([]);
    setStatus("idle");
    onTurnCompleteRef.current?.();
  }, []);

  const handleEvent = useCallback(
    (data: NexaEvent) => {
      const now = Date.now();
      switch (data.event) {
        case "ready": {
          readyRef.current = true;
          const queued = queuedRef.current;
          if (queued && wsRef.current?.readyState === WebSocket.OPEN) {
            queuedRef.current = null;
            pendingIdRef.current = null;
            setHasPending(false);
            wsRef.current.send(JSON.stringify({ message: queued }));
            setStatus("running");
            return;
          }
          setStatus(runningRef.current ? "running" : "idle");
          return;
        }

        case "turn.started":
          runningRef.current = true;
          setStatus("running");
          setPhases([]);
          return;

        case "tool.started":
          setPhases((prev) => [
            ...prev.map((p) => (p.endedAt ? p : { ...p, endedAt: now })),
            { id: nextId("phase"), label: data.label, kind: "tool", startedAt: now },
          ]);
          return;

        case "composing":
          setPhases((prev) => {
            const last = prev[prev.length - 1];
            if (last && last.kind === "composing") {
              return [...prev.slice(0, -1), { ...last, retries: (last.retries ?? 0) + 1, endedAt: undefined }];
            }
            return [
              ...prev.map((p) => (p.endedAt ? p : { ...p, endedAt: now })),
              { id: nextId("phase"), label: data.label, kind: "composing", startedAt: now },
            ];
          });
          return;

        case "turn.answer":
          settleTurn({ kind: "answer", event: data });
          return;

        case "turn.needs_input":
          settleTurn({ kind: "needs_input", event: data });
          return;

        case "turn.refused":
          settleTurn({ kind: "refused", event: data });
          return;

        case "error":
          setLastError(data.message);
          return;

        default:
          return;
      }
    },
    [settleTurn]
  );

  const open = useCallback(
    (tid: string) => {
      closeSocket();
      readyRef.current = false;
      setCloseReason(null);
      setStatus("connecting");

      const url = nexaSocketUrl(tid);
      console.info("[Nexa AI] opening socket", url);
      const ws = new WebSocket(url, [CHAT_WS_SUBPROTOCOL, sessionId]);
      wsRef.current = ws;

      ws.onopen = () => {
        if (stableTimerRef.current) clearTimeout(stableTimerRef.current);
        stableTimerRef.current = setTimeout(() => {
          stableTimerRef.current = null;
          attemptsRef.current = 0;
        }, NEXA_RECONNECT_STABLE_MS);
      };

      ws.onmessage = (event) => {
        try {
          const parsed = toNexaEvent(JSON.parse(event.data) as WireEvent);
          if (parsed) handleEvent(parsed);
        } catch (err) {
          captureException(err);
          setLastError("Unreadable message from the server.");
        }
      };

      ws.onerror = () => {
      };

      ws.onclose = (event) => {
        if (wsRef.current !== ws) return;
        console.info("[Nexa AI] socket closed", { code: event.code, reason: event.reason, wasClean: event.wasClean });
        wsRef.current = null;
        clearTimers();

        const wasRunning = runningRef.current;
        runningRef.current = false;
        readyRef.current = false;
        if (event.code === NEXA_CLOSE_UNAUTHENTICATED || event.code === NEXA_CLOSE_FORBIDDEN) {
          queuedRef.current = null;
          pendingIdRef.current = null;
          setHasPending(false);
        }
        setPhases([]);

        if (event.code === NEXA_CLOSE_UNAUTHENTICATED) {
          setCloseReason("unauthenticated");
          setStatus("closed");
          return;
        }
        if (event.code === NEXA_CLOSE_FORBIDDEN) {
          setCloseReason("forbidden");
          setStatus("closed");
          return;
        }

        if (wasRunning) setNeedsResume(true);

        if (!enabledRef.current || threadIdRef.current !== tid) return;

        if (attemptsRef.current >= NEXA_MAX_RECONNECT_ATTEMPTS) {
          captureMessage("Nexa websocket reconnect exhausted", {
            level: "warning",
            extra: { threadId: tid, attempts: attemptsRef.current },
          });
          setCloseReason("transport");
          setStatus("closed");
          return;
        }

        const attempt = attemptsRef.current;
        attemptsRef.current = attempt + 1;
        const delay = Math.min(NEXA_RECONNECT_BASE_DELAY_MS * 2 ** attempt, NEXA_RECONNECT_MAX_DELAY_MS);
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          if (enabledRef.current && threadIdRef.current === tid) openRef.current?.(tid);
        }, delay);
      };
    },
    [closeSocket, clearTimers, handleEvent, sessionId]
  );

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    const previous = prevThreadRef.current;
    prevThreadRef.current = threadId;
    if (previous === null || previous === threadId) return;

    setMessages([]);
    setPhases([]);
    setLastError(null);
    runningRef.current = false;
    queuedRef.current = null;
    pendingIdRef.current = null;
    setHasPending(false);
  }, [threadId]);

  useEffect(() => {
    if (!enabled || !threadId) {
      closeSocket();
      setStatus("connecting");
      return;
    }
    attemptsRef.current = 0;
    open(threadId);
    return () => closeSocket();
  }, [threadId, enabled, open, closeSocket]);

  const send = useCallback((text: string) => {
    const body = text.trim();
    if (!body || runningRef.current || queuedRef.current) return;

    const id = nextId("u");
    runningRef.current = true;
    setStatus("running");
    setLastError(null);
    setMessages((prev) => [...prev, { id, role: "user", text: body, at: Date.now() }]);

    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN && readyRef.current) {
      ws.send(JSON.stringify({ message: body }));
      return;
    }

    queuedRef.current = body;
    pendingIdRef.current = id;
    setHasPending(true);
  }, []);

  const cancelPending = useCallback(() => {
    const id = pendingIdRef.current;
    queuedRef.current = null;
    pendingIdRef.current = null;
    runningRef.current = false;
    setHasPending(false);
    setStatus(readyRef.current ? "idle" : "connecting");
    if (id) setMessages((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const reconnect = useCallback(() => {
    if (!threadIdRef.current) return;
    attemptsRef.current = 0;
    open(threadIdRef.current);
  }, [open]);

  const clearNeedsResume = useCallback(() => setNeedsResume(false), []);

  return { status, phases, messages, closeReason, needsResume, lastError, hasPending, send, cancelPending, reconnect, setMessages, clearNeedsResume };
}
